// オフライン計算層: API レスポンス相当の TideResponse を、
// IDB から読んだ調和定数 BLOB と Web Worker で生成する。

import type { ExtremumKind, TideResponse } from '@tideprecision/shared';
import { getStation } from '../state/idb.js';
import { computeTideInWorker } from './workerClient.js';

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function dateToJstMidnightEpoch(dateStr: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) throw new Error('invalid_date');
  const [, y, mo, d] = m;
  const utcMidnight = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  return Math.floor((utcMidnight - JST_OFFSET_MS) / 1000);
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

export async function computeTideOffline(
  stationId: number,
  date: string,
): Promise<TideResponse | null> {
  const cached = await getStation(stationId);
  if (!cached) return null;

  const epoch = dateToJstMidnightEpoch(date);
  // Worker への transfer に備えてコピーを取る（IDB の blob を保持するため）
  const blobCopy = cached.blob.slice(0);
  const { levels, extrema } = await computeTideInWorker(blobCopy, epoch);

  const extremaOut = extrema.map((e) => {
    const t = new Date((epoch + e.minute_offset * 60) * 1000);
    return {
      time: t.toISOString().replace('Z', '+00:00'),
      kind: e.kind as ExtremumKind,
      level: e.level,
    };
  });

  return {
    station: { ...cached.meta, distance_km: null },
    date,
    timezone: 'Asia/Tokyo',
    levels: Array.from(levels),
    extrema: extremaOut,
    harmonic_blob_base64: arrayBufferToBase64(cached.blob),
  };
}
